import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDialog } from "../../../utils/hooks/use-dialog";
import type { BrandKitColor, BrandKitLogo } from "../server/brand-kit-service";
import type { BrandKitManager } from "../types";
import { BrandKitChoiceRow } from "./choice-row";
import { BrandKitLogoPicker } from "./logo-picker-dialog";

export const BrandKitLogoChoiceRow: FC<{
  logos: BrandKitLogo[];
  logoManager: BrandKitManager["logo"];
  selected?: BrandKitLogo;
  selectedText?: string;
  showTitle?: boolean;
  showDefaultIndicator?: boolean;
}> = ({
  logos,
  logoManager,
  selected,
  selectedText,
  showTitle = true,
  showDefaultIndicator = true,
}) => {
  const dialog = useDialog();
  const { t } = useTranslation();
  const [editingLogo, setEditingLogo] = useState<{ logo: BrandKitLogo; index: number } | null>(
    null,
  );

  const handleEdit = (logo: BrandKitLogo, index: number): void => {
    setEditingLogo({ logo, index });
    dialog.handleOpen();
  };

  const handleAddClick = (): void => {
    if (!editingLogo) {
      setEditingLogo(null);
      dialog.handleOpen();
    }
  };

  const handleEditSubmit = (updatedLogo: BrandKitLogo): void => {
    logoManager.edit?.(updatedLogo);
    setEditingLogo(null);
  };

  const handleDialogClose = (): void => {
    dialog.handleClose();
    setEditingLogo(null);
  };

  return (
    <BrandKitChoiceRow
      title={showTitle ? t("Logos") : undefined}
      subtitle={t("We recommended using a square logo with a background color as default.")}
      type="logo"
      existing={logos ?? []}
      selected={selected}
      onClick={logoManager.add ? handleAddClick : undefined}
      remove={logoManager.remove}
      setSelected={logoManager.setSelected}
      selectedText={selectedText}
      showDefaultIndicator={showDefaultIndicator}
      addLabel={t("Add Logo")}
      onEdit={
        logoManager.edit
          ? (item: BrandKitLogo | BrandKitColor, index: number) => {
              if ("src" in item) {
                // Type guard to check if it's a BrandKitLogo
                handleEdit(item, index);
              }
            }
          : undefined
      }
    >
      {logoManager.add && (
        <BrandKitLogoPicker
          open={dialog.open}
          handleClose={handleDialogClose}
          onChange={logoManager.add}
          existingLogo={editingLogo?.logo || undefined}
          onEdit={editingLogo ? handleEditSubmit : undefined}
          onDelete={() =>
            logoManager.remove ? logoManager.remove(editingLogo?.index || 0) : undefined
          }
        />
      )}
    </BrandKitChoiceRow>
  );
};
